import type {
  ApSummary,
  EstimateAlgorithm,
  EstimateResult,
  EvaluationResult,
  Floor,
  MeasurementSource,
  ReferencePoint,
  Report,
  StoredFingerprint,
  StoredReport,
} from '@wrp/shared';

const BASE = '/api/v1';

export class ApiError extends Error {
  readonly status: number;
  readonly details: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

interface ErrorBody {
  error?: string;
  message?: string;
  details?: unknown;
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: ErrorBody | null = null;
  let text = '';
  try {
    text = await res.text();
    body = text ? (JSON.parse(text) as ErrorBody) : null;
  } catch {
    body = null;
  }
  const message = body?.error ?? body?.message ?? text ?? `${res.status} ${res.statusText}`;
  return new ApiError(message || `Request failed (${res.status})`, res.status, body?.details);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, init);
  } catch (cause) {
    const reason = cause instanceof Error ? cause.message : 'network error';
    throw new ApiError(`Cannot reach the backend: ${reason}`, 0);
  }
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

function json(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    sp.set(key, String(value));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ------------------------------------------------------------------- health

export interface HealthResponse {
  status: string;
  reports: number;
  fingerprints: number;
  floors: number;
  sse_clients: number;
}

export function getHealth(): Promise<HealthResponse> {
  return request<HealthResponse>('/health');
}

// ------------------------------------------------------------------ reports

export interface ReportListQuery {
  limit?: number;
  offset?: number;
  device_id?: string;
  source?: MeasurementSource;
  only_fingerprints?: boolean;
}

export interface ReportListResponse {
  total: number;
  items: StoredReport[];
}

export function listReports(params: ReportListQuery = {}): Promise<ReportListResponse> {
  return request<ReportListResponse>(`/reports${query({ ...params })}`);
}

export function getReport(id: string): Promise<StoredReport> {
  return request<StoredReport>(`/reports/${encodeURIComponent(id)}`);
}

export function deleteReport(id: string): Promise<void> {
  return request<void>(`/reports/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Bulk delete; without filters this removes every stored report. */
export function deleteReports(
  params: { device_id?: string; source?: MeasurementSource } = {},
): Promise<{ deleted: number }> {
  return request<{ deleted: number }>(`/reports${query({ ...params })}`, { method: 'DELETE' });
}

export interface CreateReportResponse {
  ids: string[];
  count: number;
  /** Labeled reports whose label matched no reference point on that floor. */
  unresolved_labels?: Array<{ report_id: string; floor_id: string; label: string }>;
}

export function createReport(report: Report): Promise<CreateReportResponse> {
  return request<CreateReportResponse>('/reports', json('POST', report));
}

// ------------------------------------------------------------- fingerprints

export interface FingerprintFromReferencePoint {
  reference_point_id: string;
}

export interface FingerprintFromPosition {
  position: { floor_id: string; x: number; y: number; z?: number };
  label?: string;
}

export type FingerprintCreateBody = FingerprintFromReferencePoint | FingerprintFromPosition;

export function createFingerprint(
  reportId: string,
  body: FingerprintCreateBody,
): Promise<StoredFingerprint> {
  return request<StoredFingerprint>(
    `/reports/${encodeURIComponent(reportId)}/fingerprint`,
    json('POST', body),
  );
}

export function listFingerprints(params: {
  floor_id?: string;
  source?: MeasurementSource;
} = {}): Promise<StoredFingerprint[]> {
  return request<StoredFingerprint[]>(`/fingerprints${query({ ...params })}`);
}

export function deleteFingerprint(id: string): Promise<void> {
  return request<void>(`/fingerprints/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// -------------------------------------------------------------------- floors

export interface FloorCreateBody {
  name: string;
  width_m: number;
  height_m: number;
  origin_lat?: number | null;
  origin_lon?: number | null;
  rotation_deg?: number | null;
}

export function listFloors(): Promise<Floor[]> {
  return request<Floor[]>('/floors');
}

export function createFloor(body: FloorCreateBody): Promise<Floor> {
  return request<Floor>('/floors', json('POST', body));
}

export function updateFloor(id: string, body: FloorCreateBody): Promise<Floor> {
  return request<Floor>(`/floors/${encodeURIComponent(id)}`, json('PUT', body));
}

export interface DeleteFloorResponse {
  deleted: boolean;
  /** Reports surveyed on the floor, deleted with it. */
  reports_deleted: number;
}

export function deleteFloor(id: string): Promise<DeleteFloorResponse> {
  return request<DeleteFloorResponse>(`/floors/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function uploadFloorImage(id: string, file: File): Promise<Floor> {
  const form = new FormData();
  form.append('image', file);
  return request<Floor>(`/floors/${encodeURIComponent(id)}/image`, { method: 'POST', body: form });
}

// ----------------------------------------------------------- reference points

export function listReferencePoints(floorId?: string): Promise<ReferencePoint[]> {
  return request<ReferencePoint[]>(`/reference-points${query({ floor_id: floorId })}`);
}

export function createReferencePoint(body: {
  floor_id: string;
  label: string;
  x: number;
  y: number;
  z?: number;
}): Promise<ReferencePoint> {
  return request<ReferencePoint>('/reference-points', json('POST', body));
}

export function updateReferencePoint(
  id: string,
  body: { label?: string; x?: number; y?: number; z?: number },
): Promise<ReferencePoint> {
  return request<ReferencePoint>(`/reference-points/${encodeURIComponent(id)}`, json('PATCH', body));
}

export function deleteReferencePoint(id: string): Promise<void> {
  return request<void>(`/reference-points/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// ----------------------------------------------------------------------- aps

export function listAps(): Promise<ApSummary[]> {
  return request<ApSummary[]>('/aps');
}

export function updateApPosition(
  bssid: string,
  body: { floor_id: string; x: number; y: number; z?: number },
): Promise<ApSummary> {
  return request<ApSummary>(`/aps/${encodeURIComponent(bssid)}`, json('PATCH', body));
}

export function deleteApPosition(bssid: string): Promise<void> {
  return request<void>(`/aps/${encodeURIComponent(bssid)}`, { method: 'DELETE' });
}

// --------------------------------------------------------------- positioning

export interface EstimateBody {
  report_id?: string;
  report?: Report;
  algorithm: EstimateAlgorithm;
  k: number;
  floor_id?: string;
  source?: MeasurementSource;
}

export function estimate(body: EstimateBody): Promise<EstimateResult> {
  return request<EstimateResult>('/positioning/estimate', json('POST', body));
}

export interface EvaluateBody {
  floor_id?: string;
  algorithm: EstimateAlgorithm;
  k_values?: number[];
  folds?: number;
  source?: MeasurementSource;
}

export function evaluate(body: EvaluateBody): Promise<EvaluationResult> {
  return request<EvaluationResult>('/positioning/evaluate', json('POST', body));
}

// ----------------------------------------------------------------------- sse

export const STREAM_URL = `${BASE}/stream`;
